import {
  Operation,
  OperationGroup,
  OperationPayloadCompartmentUpdate,
  OperationType,
  Pagination,
  Trigger,
} from '@ekkogmbh/apisdk';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { DataTable } from '../../Common/Components/DataTable';
import { LoadingMask } from '../../Common/Components/LoadingMask';
import { CancelableFetchPromises, cancelFetchPromises } from '../../Common/Helper/PromiseHelper';
import { TriggerCard } from './TriggerCard';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { useSearchBar } from 'src/Common/Stores/SearchBarProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';

export const CompartmentImportPayload = ({
  payload,
}: {
  payload: OperationPayloadCompartmentUpdate;
}): React.JSX.Element => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      overflowY: 'hidden',
      overflowX: 'auto',
      flexWrap: 'nowrap',
    }}
  >
    {Object.keys(payload.fields).map((key: string) => (
      <div
        key={key}
        style={{
          display: 'inline-block',
          width: 155,
          whiteSpace: 'normal',
          wordBreak: 'break-all',
        }}
      >
        <div style={{ margin: 8 }}>
          <Typography
            variant={'subtitle2'}
            color={'textSecondary'}
            style={{
              fontWeight: 700,
              wordBreak: 'keep-all',
            }}
          >
            {key}
          </Typography>
        </div>
        <div style={{ margin: 8 }}>
          <span>{payload.fields[key]}</span>
        </div>
      </div>
    ))}
  </div>
);

export const OperationGroupDetailsContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setHeaderTitle } = useSearchBar();
  const fetchPromises: CancelableFetchPromises = {};
  const { operationgroupid } = useParams();
  const navigate = useNavigate();

  const [operationGroup, setOperationGroup] = useState<OperationGroup>();

  /** on mount / unmount */
  useEffect(
    () => () => {
      cancelFetchPromises(fetchPromises);
      setHeaderTitle(undefined);
    },
    [],
  );

  /** download operation group */
  useEffect(() => {
    if (operationgroupid === undefined) {
      return;
    }
    void (async () => {
      const operationGroupId = parseInt(operationgroupid, 10);
      const og = await api.getOperationGroup(operationGroupId);
      setOperationGroup(og);
      setHeaderTitle(og.name);
    })();
  }, [operationgroupid]);

  const fetchOperations = useCallback(
    async (pagination: Pagination) => api.getOperations(operationGroup!, pagination),
    [api, fetchPromises, operationGroup],
  );

  if (operationGroup === undefined) {
    return <LoadingMask width={75} height={75} />;
  }

  return (
    <ContentControl
      panel={
        <Stack direction={'column'} spacing={0} justifyContent={'flex-start'} alignItems={'flex-start'}>
          <Stack direction={'row'}>
            <Button
              size={'small'}
              style={{
                borderRadius: 0,
              }}
              variant="text"
              color="secondary"
              onClick={() => navigate(-1)}
            >
              <KeyboardArrowLeft /> GO Back
            </Button>
            <Typography color={'primary'}>{'|'}</Typography>
            <Typography align={'center'} variant={'overline'} style={{ marginLeft: 8 }}>
              Triggers
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems={'stretch'} padding={2}>
            {operationGroup.triggers.map((trigger: Trigger, index: number) => (
              <Grid item lg={3} md={6} xs={12} key={index}>
                <TriggerCard trigger={trigger} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      }
    >
      <DataTable
        title={'Operations'}
        fetchItems={fetchOperations}
        onBack={() => navigate(-1)}
        columns={[
          {
            header: 'Coordinate',
            body: (o: Operation) => o.identifier,
            filterField: 'identifier',
            sortField: 'O.identifier',
          },
          {
            header: 'Type',
            body: (o: Operation) => o.type,
            filterField: 'type',
            sortField: 'O.type',
          },
          {
            header: 'Payload',
            body: (o: Operation) => {
              switch (o.type) {
                case OperationType.COMPARTMENT_UPDATE:
                  return <CompartmentImportPayload payload={o.payload as OperationPayloadCompartmentUpdate} />;
                default:
                  return <></>;
              }
            },
          },
        ]}
      />
    </ContentControl>
  );
};
